import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MathText } from "@/components/MathText";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Flag, ChevronLeft, ChevronRight, Send } from "lucide-react";

const STATES = {
    NOT_VISITED: "not_visited",
    NOT_ANSWERED: "not_answered",
    ANSWERED: "answered",
    MARKED: "marked",
    ANSWERED_MARKED: "answered_marked",
};

const STATE_STYLES = {
    [STATES.NOT_VISITED]: "bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200",
    [STATES.NOT_ANSWERED]: "bg-red-500 text-white",
    [STATES.ANSWERED]: "bg-green-600 text-white",
    [STATES.MARKED]: "bg-yellow-500 text-white",
    [STATES.ANSWERED_MARKED]: "bg-purple-600 text-white",
};

export default function ExamInterface() {
    const { sessionId } = useParams();
    const nav = useNavigate();
    const [session, setSession] = useState(null);
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // qid -> {selected, marked}
    const [visited, setVisited] = useState(new Set([0]));
    const [times, setTimes] = useState({});
    const [remaining, setRemaining] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const questionStartRef = useRef(Date.now());

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/exam/session/${sessionId}`);
                if (r.data.status === "submitted") { nav(`/result/${sessionId}`, { replace: true }); return; }
                setSession(r.data);
                setRemaining(r.data.duration_minutes * 60);
            } catch (e) { toast.error("Failed to load session"); nav("/dashboard"); }
        })();
    }, [sessionId, nav]);

    // Timer
    useEffect(() => {
        if (!session || remaining <= 0) return;
        const t = setInterval(() => setRemaining((r) => r - 1), 1000);
        return () => clearInterval(t);
    }, [session, remaining]);

    // Auto-submit at 0
    useEffect(() => {
        if (session && remaining === 0) {
            handleSubmit(true);
        }
        // eslint-disable-next-line
    }, [remaining, session]);

    const questions = session?.questions || [];
    const current = questions[idx];

    // Track time when moving away from a question
    const recordTime = (qid) => {
        const elapsed = Math.round((Date.now() - questionStartRef.current) / 1000);
        setTimes((t) => ({ ...t, [qid]: (t[qid] || 0) + elapsed }));
        questionStartRef.current = Date.now();
    };

    const goTo = (i) => {
        if (i < 0 || i >= questions.length) return;
        if (current) recordTime(current.id);
        setIdx(i);
        setVisited((v) => new Set([...v, i]));
    };

    const selectOption = (opt) => {
        setAnswers((a) => ({ ...a, [current.id]: { ...(a[current.id] || {}), selected: opt } }));
    };

    const clearAnswer = () => {
        setAnswers((a) => {
            const cur = { ...(a[current.id] || {}) };
            delete cur.selected;
            return { ...a, [current.id]: cur };
        });
    };

    const toggleMark = () => {
        setAnswers((a) => ({ ...a, [current.id]: { ...(a[current.id] || {}), marked: !a[current.id]?.marked } }));
    };

    const stateFor = (i) => {
        const q = questions[i];
        if (!visited.has(i)) return STATES.NOT_VISITED;
        const a = answers[q.id];
        if (a?.selected && a?.marked) return STATES.ANSWERED_MARKED;
        if (a?.selected) return STATES.ANSWERED;
        if (a?.marked) return STATES.MARKED;
        return STATES.NOT_ANSWERED;
    };

    const counts = useMemo(() => {
        const c = { answered: 0, not_answered: 0, not_visited: 0, marked: 0, answered_marked: 0 };
        questions.forEach((_, i) => { c[stateFor(i)] += 1; });
        return c;
        // eslint-disable-next-line
    }, [questions, answers, visited]);

    const handleSubmit = async (auto = false) => {
        if (submitting) return;
        if (current) recordTime(current.id);
        setSubmitting(true);
        try {
            const payload = {
                answers: questions.map((q) => ({
                    question_id: q.id,
                    selected_option: answers[q.id]?.selected || null,
                    marked_for_review: !!answers[q.id]?.marked,
                    time_spent_seconds: times[q.id] || 0,
                })),
            };
            await api.post(`/exam/session/${sessionId}/submit`, payload);
            if (auto) toast.info("Time up — submitted automatically");
            else toast.success("Exam submitted");
            nav(`/result/${sessionId}`, { replace: true });
        } catch (e) {
            toast.error("Submit failed");
        } finally { setSubmitting(false); }
    };

    if (!session) return <div className="min-h-[70vh] flex items-center justify-center font-mono text-muted-foreground">Loading exam…</div>;
    if (!current) return null;

    const mins = String(Math.floor(remaining / 60)).padStart(2, "0");
    const secs = String(remaining % 60).padStart(2, "0");

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
            {/* Header */}
            <div className="flex items-center justify-between border border-border rounded-sm p-4 mb-4">
                <div>
                    <span className="overline text-primary">▮ {session.exam} · {session.difficulty}</span>
                    <div className="font-heading font-bold text-lg mt-1">Question {idx + 1}<span className="text-muted-foreground text-sm">/{questions.length}</span></div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        <span data-testid="exam-timer" className={`font-mono font-bold text-2xl ${remaining < 60 ? "text-primary animate-pulse" : ""}`}>{mins}:{secs}</span>
                    </div>
                    <Button data-testid="exam-submit-btn" onClick={() => handleSubmit(false)} disabled={submitting}
                        className="rounded-sm bg-primary hover:bg-primary/90 btn-press">
                        <Send className="w-4 h-4 mr-2" /> Submit exam
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-4">
                {/* LEFT — question */}
                <div className="lg:col-span-8 border border-border rounded-sm p-6 min-h-[60vh] flex flex-col">
                    <div className="flex items-center gap-2 text-xs font-mono">
                        <span className="px-2 py-0.5 border border-border rounded-sm">{current.subject}</span>
                        <span className="px-2 py-0.5 border border-border rounded-sm">{current.topic}</span>
                        <span className="px-2 py-0.5 border border-border rounded-sm capitalize">{current.difficulty}</span>
                    </div>

                    <MathText className="mt-6 text-base sm:text-lg leading-relaxed" data-testid="exam-question-text">
                        {current.question}
                    </MathText>

                    <div className="mt-8 space-y-3 flex-1">
                        {current.options.map((opt) => {
                            const selected = answers[current.id]?.selected === opt.key;
                            return (
                                <button key={opt.key} data-testid={`exam-option-${opt.key}`} onClick={() => selectOption(opt.key)}
                                    className={`w-full text-left border rounded-sm p-4 flex items-center gap-4 btn-press transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
                                    <span className={`w-8 h-8 rounded-sm border flex items-center justify-center font-mono font-bold ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                                        {opt.key}
                                    </span>
                                    <MathText inline className="flex-1">{opt.text}</MathText>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-2 pt-4 border-t border-border">
                        <Button data-testid="exam-prev-btn" variant="outline" className="rounded-sm btn-press" onClick={() => goTo(idx - 1)} disabled={idx === 0}>
                            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                        </Button>
                        <Button data-testid="exam-mark-btn" variant="outline" onClick={toggleMark} className="rounded-sm btn-press">
                            <Flag className="w-4 h-4 mr-2" /> {answers[current.id]?.marked ? "Unmark" : "Mark for review"}
                        </Button>
                        <Button data-testid="exam-clear-btn" variant="ghost" onClick={clearAnswer} className="rounded-sm">Clear</Button>
                        <div className="flex-1" />
                        <Button data-testid="exam-save-next-btn" onClick={() => goTo(idx + 1)} disabled={idx === questions.length - 1}
                            className="rounded-sm bg-secondary hover:bg-secondary/90 text-secondary-foreground btn-press">
                            Save & Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>

                {/* RIGHT — palette */}
                <aside className="lg:col-span-4 border border-border rounded-sm p-4 h-fit sticky top-20">
                    <span className="overline text-muted-foreground">Question palette</span>
                    <div className="mt-4 grid grid-cols-6 gap-1.5">
                        {questions.map((_, i) => {
                            const st = stateFor(i);
                            return (
                                <button key={i} data-testid={`palette-btn-${i}`} onClick={() => goTo(i)}
                                    className={`w-full aspect-square font-mono font-bold text-xs rounded-sm border ${STATE_STYLES[st]} ${i === idx ? "ring-2 ring-primary" : ""} btn-press`}>
                                    {i + 1}
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-6 space-y-1.5 text-xs font-mono">
                        <LegendRow color="bg-green-600" label={`Answered · ${counts.answered}`} />
                        <LegendRow color="bg-red-500" label={`Not answered · ${counts.not_answered}`} />
                        <LegendRow color="bg-yellow-500" label={`Marked · ${counts.marked}`} />
                        <LegendRow color="bg-purple-600" label={`Answered + Marked · ${counts.answered_marked}`} />
                        <LegendRow color="bg-zinc-300 dark:bg-zinc-700" label={`Not visited · ${counts.not_visited}`} />
                    </div>
                </aside>
            </div>
        </div>
    );
}

const LegendRow = ({ color, label }) => (
    <div className="flex items-center gap-2">
        <span className={`inline-block w-3 h-3 rounded-sm ${color}`} />
        <span className="text-muted-foreground">{label}</span>
    </div>
);
